import React, { useEffect, useRef, useState } from "react";
import PropTypes from 'prop-types';
import { Modal, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import ImageWithBasePath from "../../img/imagewithbasebath";
import { fetchDataRequest, fetchDataSuccess } from "../../redux/action";
import SignalRClient from "../../../services/SignalRClient";
import { Payment } from "../../../services/internal/payment";
import { useForm } from "react-hook-form";
import variables from "../../../style/scss/utils/_variables.scss";
import { DollarSign } from "react-feather";
import TokenRefresh from "../../../Router/tokenRefresh";

const MercadoPagoModal = ({ id, show = false, onHide, saleCode, saleTotalAmount, onSubmit }) => {

    const { register, handleSubmit, formState: { errors } } = useForm();
    const dispatch = useDispatch();
    const token = useSelector((state) => state.token);
    const loading = useSelector((state) => state.loading);
    const userDetails = useSelector((state) => state.userDetails);
    const [showSmartPointImage, setShowSmartPointImage] = useState(false);
    const [paymentSuccess, setPaymentSuccess] = useState(false);
    const [statusText, setStatusText] = useState("");
    let abortController = new AbortController();
    const signalRClientRef = useRef(null);
    const tokenRefreshRef = useRef(null);
    const intentIdRef = useRef(null);
    const notifiedRef = useRef(false);

    const onProcessMp = async (formData) => {
        try {
            dispatch(fetchDataRequest());

            setShowSmartPointImage(false);
            setStatusText("Procesando pago con Mercado Pago...");
            let data = Payment.requestData;
            data = {};
            data.amount = saleTotalAmount;
            data.printReceipt = formData.printReceipt === "activo";
            data.saleCode = saleCode;
            data.branchName = userDetails?.branch?.name;

            const response = await Payment.postMpProcessPointFlow(token, data, abortController);

            if (response.data.success) {
                intentIdRef.current = response.data.data;
                setShowSmartPointImage(true);
                setStatusText("Esperando cobro en SmartPoint...");
            }
        }
        catch (err) {
            setStatusText("Por favor verifica la configuración de Mercado Pago con tu administrador e intenta nuevamente.");
            if (tokenRefreshRef.current) {
                tokenRefreshRef.current.handleException(err);
            }
        }
    }

    const onModalHide = () => {
        onHide();
    }

    useEffect(() => {
        // Función asíncrona para inicializar la conexión
        const initializeSignalR = async () => {
            const baseUrl = process.env.REACT_APP_BASE_URL_API;
            const relativePath = '/notification';
            const client = new SignalRClient(baseUrl, relativePath);
            signalRClientRef.current = client;

            await client.start();

            // Registro de manejador para recibir mensajes en formato JSON
            client.on('ReceiveMessage', (data) => {
                console.log(data)
                setShowSmartPointImage(false);
                // data es un objeto JSON con propiedades: user, message y timestamp
                if (data?.intentId === intentIdRef.current) {
                    if (data?.paymentEstatus === Payment.MpState.Finished) {
                        setStatusText("Pago exitoso por Mercado Pago.");
                        setPaymentSuccess(true);
                        if (!notifiedRef.current) {
                            notifiedRef.current = true;
                            onSubmit(intentIdRef.current);
                        }
                    } else {
                        setStatusText("Ups!...No logramos procesar el pago con Mercado Pago. Intenta nuevamente por favor.");
                    }
                    dispatch(fetchDataSuccess());
                }
            });
        };

        initializeSignalR();

        // Cleanup: detener la conexión al desmontar el componente
        return () => {
            if (signalRClientRef.current) {
                signalRClientRef.current.stop();
            }
        };
    }, []);

    useEffect(() => {
        // Cleanup: detener llamdas asincronas
        abortController = new AbortController();
        return () => {
            abortController.abort();
        };
    }, [token]);


    return (
        <Modal show={show} centered onHide={onModalHide} id={id} backdrop="static" keyboard={false}>
            <form onSubmit={handleSubmit((data) => onProcessMp(data))}>
                <Modal.Header className="custom-modal-header" closeButton={!loading}>
                    <h4>Pago con tarjeta débito/crédito</h4>
                </Modal.Header>
                <Modal.Body className="text-center">
                    <Modal.Title className='justify-content-center mb-4'>
                        <ImageWithBasePath
                            style={{ width: 200, padding: 5 }}
                            src="assets/img/delivery/mp-color-azul_hori-izq.svg"
                            alt="Mercado Pago"
                        />
                    </Modal.Title>
                    <div className="row mb-3">
                        <div className="col-6 text-end">
                            <label className="col-form-label">Vamos a pagar:</label>
                        </div>
                        <div className="col-6">
                            <div className="input-group">
                                <span className="input-group-text"><DollarSign size={15} color={variables.textColor} /></span>
                                <input
                                    type="text"
                                    className="form-control text-end"
                                    id="saleTotalAmount"
                                    value={saleTotalAmount?.toFixed(2)}
                                    disabled
                                />
                            </div>
                        </div>
                    </div>
                    <div className="row mb-3">
                        <div className="col-6 text-end">
                            <label className="form-label me-1">¿Imprimir recibo en SmartPoint?<span className="text-danger"> *</span></label>
                        </div>
                        <div className="col-6 text-start">
                            <div className="form-check">
                                <input
                                    className={!errors.printReceipt ? "form-check-input" : "form-check-input is-invalid"}
                                    id="estatus_activo"
                                    type="radio"
                                    value="activo"
                                    {...register("printReceipt",
                                        {
                                            required: "Es necesario marcar este campo."
                                        })
                                    }
                                    disabled={loading}
                                />
                                <label className="form-check-label" htmlFor="estatus_activo">Si</label>
                            </div>
                            <div className="form-check">
                                <input
                                    className={!errors.printReceipt ? "form-check-input" : "form-check-input is-invalid"}
                                    id="estatus_inactivo"
                                    type="radio"
                                    value="inactivo"
                                    {...register("printReceipt")}
                                    disabled={loading}
                                />
                                <label className="form-check-label" htmlFor="estatus_inactivo">No</label>
                                {
                                    errors.printReceipt ?
                                        <div className="invalid-feedback">
                                            {errors.printReceipt.message}
                                        </div>
                                        : null
                                }
                            </div>
                        </div>
                    </div>
                    {showSmartPointImage &&
                        <ImageWithBasePath
                            style={{ height: 300, padding: 5 }}
                            src="assets/img/delivery/smartpoint.jpg"
                            alt="Mercado Pago"
                        />
                    }
                    <p className="text-dark fw-bold">{statusText}</p>
                </Modal.Body>
                <Modal.Footer className='justify-content-center'>
                    <button
                        className="btn btn-cancel me-2"
                        onClick={(e) => onModalHide(e, false)}
                        disabled={loading}
                    >
                        Regresar
                    </button>
                    {
                        loading || paymentSuccess ?
                            <button disabled={loading} className="btn btn-submit">
                                <Spinner animation="border" role="status" size="sm" />
                            </button>
                            :
                            <input
                                type="submit"
                                className="btn btn-dark"
                                disabled={loading}
                                value="Procesar Orden"
                            />
                    }
                </Modal.Footer>
            </form>
            <TokenRefresh ref={tokenRefreshRef} />
        </Modal>
    );
};

MercadoPagoModal.propTypes = {
    id: PropTypes.string.isRequired,
    show: PropTypes.bool,
    onHide: PropTypes.func,
    onSubmit: PropTypes.func,
    saleCode: PropTypes.string.isRequired,
    saleTotalAmount: PropTypes.number.isRequired,
};


export default MercadoPagoModal;
